// utils/validators.ts

// Regex to IPv4 (0.0.0.0 a 255.255.255.255)
const IPV4_REGEX =
  /^(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

// Regex to IPv6
const IPV6_REGEX =
  /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;

// Regex for domains (ex: google.com, sub.site.com.br, localhost)
const DOMAIN_REGEX =
  /^(localhost)|(([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,})$/;

/**
 * Clean entry removing protocols and paths from plain host inputs.
 * If the input is a full URL (starts with http:// or https://) it is
 * returned as-is so the backend receives the complete URL.
 * Ex: 'google.com/search'        → 'google.com'
 *     'http://8.8.8.8'           → 'http://8.8.8.8'   (preserved)
 *     'https://example.com/path' → 'https://example.com/path' (preserved)
 */
export const sanitizeHost = (input: string): string => {
  const trimmed = input.trim();
  // If the user typed a full URL, send it as-is
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Otherwise strip any accidentally pasted protocol / path
  return trimmed.replace(/^(https?:\/\/)/, '').replace(/\/.*$/, '').trim();
};

/**
 * Checks if it's a valid IP, domain, or full URL (http:// / https://)
 */
export const isValidHost = (input: string): boolean => {
  const trimmed = input.trim();
  // Accept full URLs
  if (/^https?:\/\/.+/i.test(trimmed)) return true;
  const host = sanitizeHost(trimmed);
  return (
    IPV4_REGEX.test(host) || IPV6_REGEX.test(host) || DOMAIN_REGEX.test(host)
  );
};
